import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getLocalizedText } from '../utils/helpers';
import { 
  Settings, AlertTriangle, Users, BookOpen, Star, Flag, Pause, Heart, GraduationCap, FileText
} from 'lucide-react';
import axios from 'axios';

// Import refactored components
import {
  StatCard,
  DetailPanel,
  DeleteDialog,
  AdminActionDialog,
  ReportReviewDialog,
  ResetPointsDialog,
  ReportDetailDialog,
  CreateUserDialog,
  ImpersonateDialog,
  PendingTab,
  EditsTab,
  GoogleRatingsTab,
  ReportsTab,
  UsersTab,
  ContentTab,
  PausedTab,
  BlogTab
} from './admin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders, setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [data, setData] = useState({ 
    teachers: [], schools: [], services: [], courses: [], reviews: [], users: [], avatars: [],
    allServices: [], allCourses: [], pendingEdits: { services: [], courses: [], teachers: [], schools: [] },
    pausedServices: [], pausedCourses: [],
    pendingGoogleRatings: { teachers: [], schools: [] }
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Content filters
  const [contentSearch, setContentSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [contentSortConfig, setContentSortConfig] = useState({ key: 'title', direction: 'asc' });
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsStats, setReportsStats] = useState({ total: 0, pending: 0, verified: 0, infractions: 0, null_reports: 0 });
  const [reportsFilter, setReportsFilter] = useState('pending');
  
  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({ open: false, endpoint: '', itemName: '', itemType: '' });
  const [adminDialog, setAdminDialog] = useState({ open: false, action: '', userId: '', userName: '' });
  const [reportDialog, setReportDialog] = useState({ open: false, reportId: '', outcome: '', reporterName: '', reportedName: '' });
  const [reportDetailDialog, setReportDetailDialog] = useState({ open: false, report: null });
  const [resetPointsDialog, setResetPointsDialog] = useState({ open: false, userId: '', userName: '' });
  const [createUserDialog, setCreateUserDialog] = useState({ open: false, loading: false });
  const [impersonateDialog, setImpersonateDialog] = useState({ open: false, userId: '', userName: '' });
  
  // Preview panel state
  const [previewPanel, setPreviewPanel] = useState({ 
    open: false, 
    item: null, 
    type: null,
    approveEndpoint: '',
    rejectEndpoint: ''
  });

  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAuthenticated, user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [teachers, schools, services, courses, reviews, users, avatars, allServices, allCourses, pendingEdits, pendingGoogleRatings, reportsRes, reportsStatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/pending-teachers`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-schools`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-services`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-courses`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-reviews`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-avatars`, { headers }),
        axios.get(`${API_URL}/api/admin/all-services`, { headers }),
        axios.get(`${API_URL}/api/admin/all-courses`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-edits`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-google-ratings`, { headers }),
        axios.get(`${API_URL}/api/reports/admin/all`, { headers }),
        axios.get(`${API_URL}/api/reports/admin/stats`, { headers }),
      ]);
      
      const pausedServices = allServices.data.filter(s => !s.is_active && s.deactivated_by_user);
      const pausedCourses = allCourses.data.filter(c => !c.is_active && c.deactivated_by_user);
      
      setData({
        teachers: teachers.data,
        schools: schools.data,
        services: services.data,
        courses: courses.data,
        reviews: reviews.data,
        users: users.data,
        avatars: avatars.data,
        allServices: allServices.data,
        allCourses: allCourses.data,
        pendingEdits: pendingEdits.data,
        pausedServices,
        pausedCourses,
        pendingGoogleRatings: pendingGoogleRatings.data,
      });
      
      setReports(reportsRes.data || []);
      setReportsStats(reportsStatsRes.data || { total: 0, pending: 0, verified: 0, infractions: 0, null_reports: 0 });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // Action handlers
  const handleAction = async (endpoint, method = 'put', body = {}) => {
    try {
      if (method === 'put') {
        await axios.put(`${API_URL}${endpoint}`, body, { headers: getAuthHeaders() });
      } else if (method === 'delete') {
        await axios.delete(`${API_URL}${endpoint}`, { headers: getAuthHeaders() });
      }
      loadData();
    } catch (e) {
      alert('Error al procesar la acción');
    }
  };

  const handleDeleteWithConfirm = (endpoint, itemName, itemType = 'elemento') => {
    setDeleteDialog({ open: true, endpoint, itemName, itemType });
  };

  const confirmDelete = async () => {
    await handleAction(deleteDialog.endpoint, 'delete');
    setDeleteDialog({ open: false, endpoint: '', itemName: '', itemType: '' });
  };

  // Report handlers
  const handleReportReview = (reportId, outcome, reporterName = '', reportedName = '') => {
    setReportDialog({ open: true, reportId, outcome, reporterName, reportedName });
  };

  const confirmReportReview = async () => {
    const { reportId, outcome } = reportDialog;
    try {
      const response = await axios.put(
        `${API_URL}/api/reports/admin/${reportId}/review`,
        { outcome },
        { headers: getAuthHeaders() }
      );
      setReportDialog({ open: false, reportId: '', outcome: '', reporterName: '', reportedName: '' });
      if (response.data.user_suspended) {
        alert('L\'utente ha raggiunto 3 punti negativi ed è stato SOSPESO automaticamente.');
      }
      loadData();
    } catch (e) {
      console.error('Error reviewing report:', e);
      alert('Errore durante la verifica della segnalazione');
    }
  };

  const handleResetUserPoints = (userId, userName) => {
    setResetPointsDialog({ open: true, userId, userName });
  };

  const confirmResetUserPoints = async () => {
    const { userId } = resetPointsDialog;
    try {
      await axios.put(
        `${API_URL}/api/reports/admin/user/${userId}/reset-points`,
        {},
        { headers: getAuthHeaders() }
      );
      setResetPointsDialog({ open: false, userId: '', userName: '' });
      loadData();
    } catch (e) {
      console.error('Error resetting points:', e);
      alert('Errore durante il reset dei punti');
    }
  };

  // User management handlers
  const handleDeleteUser = (userId, userName) => {
    setDeleteDialog({ 
      open: true, 
      endpoint: `/api/admin/users/${userId}`, 
      itemName: userName, 
      itemType: 'usuario (y todos sus servicios, cursos y datos)' 
    });
  };

  const toggleUserActive = async (userId, userName) => {
    const userToToggle = data.users.find(u => u.id === userId);
    const isCurrentlyActive = userToToggle?.is_active !== false;
    setAdminDialog({
      open: true,
      action: isCurrentlyActive ? 'disable' : 'enable',
      userId,
      userName
    });
  };

  const handlePromoteAdmin = (userId, userName) => {
    setAdminDialog({ open: true, action: 'promote', userId, userName });
  };

  const handleRevokeAdmin = (userId, userName) => {
    setAdminDialog({ open: true, action: 'revoke', userId, userName });
  };

  const confirmAdminAction = async () => {
    const { action, userId } = adminDialog;
    try {
      if (action === 'promote') {
        await axios.put(`${API_URL}/api/admin/users/${userId}/promote-admin`, {}, { headers: getAuthHeaders() });
      } else if (action === 'revoke') {
        await axios.put(`${API_URL}/api/admin/users/${userId}/revoke-admin`, {}, { headers: getAuthHeaders() });
      } else if (action === 'disable' || action === 'enable') {
        await axios.put(`${API_URL}/api/admin/users/${userId}/toggle-active`, {}, { headers: getAuthHeaders() });
      }
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al procesar acción');
    }
    setAdminDialog({ open: false, action: '', userId: '', userName: '' });
  };

  // Create User handlers
  const handleCreateUser = () => {
    setCreateUserDialog({ open: true, loading: false });
  };

  const confirmCreateUser = async (formData) => {
    setCreateUserDialog(prev => ({ ...prev, loading: true }));
    try {
      await axios.post(`${API_URL}/api/admin/users/create`, formData, { headers: getAuthHeaders() });
      setCreateUserDialog({ open: false, loading: false });
      loadData();
      alert(`Usuario ${formData.name} creado exitosamente. Se ha enviado un email con las credenciales.`);
    } catch (e) {
      setCreateUserDialog(prev => ({ ...prev, loading: false }));
      alert(e.response?.data?.detail || 'Error al crear usuario');
    }
  };

  // Impersonate handlers
  const handleImpersonate = (userId, userName) => {
    setImpersonateDialog({ open: true, userId, userName });
  };

  const confirmImpersonate = async () => {
    const { userId } = impersonateDialog;
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/impersonate/${userId}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Store the admin's original token for later
      const adminToken = localStorage.getItem('token');
      localStorage.setItem('admin_original_token', adminToken);
      
      // Update auth with the impersonated user's token
      setAuth(response.data.token, response.data.user);
      
      setImpersonateDialog({ open: false, userId: '', userName: '' });
      
      // Redirect to home page as the impersonated user
      navigate('/');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al impersonar usuario');
    }
  };

  // Preview panel handlers
  const openPreview = (item, type, approveEndpoint, rejectEndpoint = null) => {
    setPreviewPanel({ 
      open: true, 
      item, 
      type, 
      approveEndpoint,
      rejectEndpoint: rejectEndpoint || approveEndpoint.replace('/approve', '/reject').replace('/approve-edit', '/reject-edit')
    });
  };

  const handleApproveFromPreview = async () => {
    await handleAction(previewPanel.approveEndpoint, 'put');
    setPreviewPanel({ open: false, item: null, type: null, approveEndpoint: '', rejectEndpoint: '' });
  };

  const handleRejectFromPreview = async () => {
    await handleAction(previewPanel.rejectEndpoint, 'put');
    setPreviewPanel({ open: false, item: null, type: null, approveEndpoint: '', rejectEndpoint: '' });
  };

  // Content handlers
  const toggleFeatured = async (type, id, currentStatus) => {
    const endpoint = `/api/admin/${type}/${id}/feature?featured=${!currentStatus}`;
    await handleAction(endpoint, 'put');
  };

  const toggleServiceActive = async (serviceId) => {
    await handleAction(`/api/admin/services/${serviceId}/toggle-active`, 'put');
  };

  const toggleCourseActive = async (courseId) => {
    await handleAction(`/api/admin/courses/${courseId}/toggle-active`, 'put');
  };

  // Sorting handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleContentSort = (key) => {
    setContentSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Memoized filtered data
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = data.users.filter(u => {
      if (searchQuery && !u.name?.toLowerCase().includes(searchQuery.toLowerCase()) && 
          !u.email?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (userFilter === 'admin' && !u.roles?.includes('admin')) return false;
      if (userFilter === 'disabled' && u.is_active !== false) return false;
      if (userFilter === 'teachers' && !u.roles?.includes('teacher')) return false;
      if (userFilter === 'schools' && !u.roles?.includes('school')) return false;
      return true;
    });
    
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [data.users, searchQuery, userFilter, sortConfig]);

  const filteredAndSortedServices = useMemo(() => {
    let filtered = data.allServices.filter(s => {
      const title = getLocalizedText(s.title, 'es') || '';
      const teacherName = s.teacher_name || s.user_name || '';
      if (contentSearch && 
          !title.toLowerCase().includes(contentSearch.toLowerCase()) && 
          !teacherName.toLowerCase().includes(contentSearch.toLowerCase())) {
        return false;
      }
      if (serviceFilter === 'featured' && !s.is_featured && !s.featured) return false;
      if (serviceFilter === 'active' && !s.is_active) return false;
      if (serviceFilter === 'inactive' && s.is_active) return false;
      return true;
    });
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (contentSortConfig.key === 'title') {
        aVal = getLocalizedText(a.title, 'es') || '';
        bVal = getLocalizedText(b.title, 'es') || '';
      } else if (contentSortConfig.key === 'price') {
        aVal = a.price || 0;
        bVal = b.price || 0;
      } else if (contentSortConfig.key === 'created_at') {
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
      } else {
        aVal = a[contentSortConfig.key] || '';
        bVal = b[contentSortConfig.key] || '';
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return contentSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return contentSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [data.allServices, contentSearch, serviceFilter, contentSortConfig]);

  const filteredAndSortedCourses = useMemo(() => {
    let filtered = data.allCourses.filter(c => {
      const title = getLocalizedText(c.title, 'es') || '';
      const schoolName = c.school_name || c.user_name || '';
      if (contentSearch && 
          !title.toLowerCase().includes(contentSearch.toLowerCase()) && 
          !schoolName.toLowerCase().includes(contentSearch.toLowerCase())) {
        return false;
      }
      if (courseFilter === 'featured' && !c.is_featured && !c.featured) return false;
      if (courseFilter === 'active' && !c.is_active) return false;
      if (courseFilter === 'inactive' && c.is_active) return false;
      return true;
    });
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (contentSortConfig.key === 'title') {
        aVal = getLocalizedText(a.title, 'es') || '';
        bVal = getLocalizedText(b.title, 'es') || '';
      } else if (contentSortConfig.key === 'price') {
        aVal = a.price || 0;
        bVal = b.price || 0;
      } else if (contentSortConfig.key === 'start_date') {
        aVal = new Date(a.start_date || 0).getTime();
        bVal = new Date(b.start_date || 0).getTime();
      } else {
        aVal = a[contentSortConfig.key] || '';
        bVal = b[contentSortConfig.key] || '';
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return contentSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return contentSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [data.allCourses, contentSearch, courseFilter, contentSortConfig]);

  if (!isAuthenticated || !isAdmin) return null;

  // Counts
  const pendingNewCount = data.teachers.length + data.schools.length + data.services.length + data.courses.length + data.reviews.length + data.avatars.length;
  const pendingEditsCount = (data.pendingEdits?.services?.length || 0) + (data.pendingEdits?.courses?.length || 0) + (data.pendingEdits?.teachers?.length || 0) + (data.pendingEdits?.schools?.length || 0);
  const pausedCount = (data.pausedServices?.length || 0) + (data.pausedCourses?.length || 0);
  const pendingGoogleRatingsCount = (data.pendingGoogleRatings?.teachers?.length || 0) + (data.pendingGoogleRatings?.schools?.length || 0);
  const activeUsersCount = data.users.filter(u => u.is_active !== false).length;

  // Tabs configuration
  const tabs = [
    { id: 'pending', label: 'Nuevos', icon: Settings, count: pendingNewCount, color: 'red' },
    { id: 'edits', label: 'Modifiche', icon: AlertTriangle, count: pendingEditsCount, color: 'orange' },
    { id: 'google-ratings', label: 'Google', icon: Star, count: pendingGoogleRatingsCount, color: 'yellow' },
    { id: 'reports', label: 'Segnalazioni', icon: Flag, count: reportsStats.pending, color: 'red' },
    { id: 'users', label: 'Usuarios', icon: Users, count: null },
    { id: 'content', label: 'Contenidos', icon: BookOpen, count: null },
    { id: 'paused', label: 'Pausados', icon: Pause, count: pausedCount, color: 'gray' },
    { id: 'blog', label: 'Blog', icon: FileText, count: null },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Preview Panel */}
      {previewPanel.open && (
        <DetailPanel 
          item={previewPanel.item}
          type={previewPanel.type}
          onApprove={handleApproveFromPreview}
          onReject={handleRejectFromPreview}
          onClose={() => setPreviewPanel({ open: false, item: null, type: null, approveEndpoint: '', rejectEndpoint: '' })}
        />
      )}
      
      {/* Dialogs */}
      <DeleteDialog
        open={deleteDialog.open}
        itemName={deleteDialog.itemName}
        itemType={deleteDialog.itemType}
        onCancel={() => setDeleteDialog({ open: false, endpoint: '', itemName: '', itemType: '' })}
        onConfirm={confirmDelete}
      />

      <AdminActionDialog
        open={adminDialog.open}
        action={adminDialog.action}
        userName={adminDialog.userName}
        onCancel={() => setAdminDialog({ open: false, action: '', userId: '', userName: '' })}
        onConfirm={confirmAdminAction}
      />

      <ReportReviewDialog
        open={reportDialog.open}
        outcome={reportDialog.outcome}
        reporterName={reportDialog.reporterName}
        reportedName={reportDialog.reportedName}
        onCancel={() => setReportDialog({ open: false, reportId: '', outcome: '', reporterName: '', reportedName: '' })}
        onConfirm={confirmReportReview}
      />

      <ResetPointsDialog
        open={resetPointsDialog.open}
        userName={resetPointsDialog.userName}
        onCancel={() => setResetPointsDialog({ open: false, userId: '', userName: '' })}
        onConfirm={confirmResetUserPoints}
      />

      <CreateUserDialog
        open={createUserDialog.open}
        loading={createUserDialog.loading}
        onCancel={() => setCreateUserDialog({ open: false, loading: false })}
        onConfirm={confirmCreateUser}
      />

      <ImpersonateDialog
        open={impersonateDialog.open}
        userName={impersonateDialog.userName}
        onCancel={() => setImpersonateDialog({ open: false, userId: '', userName: '' })}
        onConfirm={confirmImpersonate}
      />

      <ReportDetailDialog
        open={reportDetailDialog.open}
        report={reportDetailDialog.report}
        onClose={() => setReportDetailDialog({ open: false, report: null })}
        onReviewInfraction={() => {
          setReportDetailDialog({ open: false, report: null });
          handleReportReview(
            reportDetailDialog.report.id, 
            'infraction', 
            reportDetailDialog.report.reporter_name, 
            reportDetailDialog.report.reported_user_name
          );
        }}
        onReviewNullReport={() => {
          setReportDetailDialog({ open: false, report: null });
          handleReportReview(
            reportDetailDialog.report.id, 
            'null_report', 
            reportDetailDialog.report.reporter_name, 
            reportDetailDialog.report.reported_user_name
          );
        }}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-gray-800 mb-2">Panel de Administración</h1>
          <p className="text-gray-500">Gestiona usuarios, contenidos y aprobaciones</p>
        </div>
        
        {/* Stats Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Usuarios Activos" value={activeUsersCount} color="green" />
          <StatCard icon={Heart} label="Servicios" value={data.allServices.length} color="primary" />
          <StatCard icon={GraduationCap} label="Cursos" value={data.allCourses.length} color="primary" />
          <StatCard 
            icon={AlertTriangle} 
            label="Pendientes" 
            value={pendingNewCount + pendingEditsCount} 
            color={pendingNewCount + pendingEditsCount > 0 ? 'red' : 'green'} 
            onClick={() => setActiveTab('pending')}
          />
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="flex overflow-x-auto border-b">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'border-primary text-primary bg-primary/5' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full text-white ${
                    tab.color === 'red' ? 'bg-red-500' : 
                    tab.color === 'orange' ? 'bg-orange-500' : 
                    tab.color === 'yellow' ? 'bg-yellow-500' : 
                    'bg-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">Cargando...</p>
              </div>
            ) : (
              <>
                {activeTab === 'pending' && (
                  <PendingTab 
                    data={data}
                    pendingNewCount={pendingNewCount}
                    onOpenPreview={openPreview}
                    onAction={handleAction}
                  />
                )}

                {activeTab === 'edits' && (
                  <EditsTab 
                    pendingEdits={data.pendingEdits}
                    pendingEditsCount={pendingEditsCount}
                    onOpenPreview={openPreview}
                    onAction={handleAction}
                  />
                )}

                {activeTab === 'google-ratings' && (
                  <GoogleRatingsTab 
                    pendingGoogleRatings={data.pendingGoogleRatings}
                    pendingGoogleRatingsCount={pendingGoogleRatingsCount}
                    onAction={handleAction}
                  />
                )}

                {activeTab === 'reports' && (
                  <ReportsTab 
                    reports={reports}
                    reportsStats={reportsStats}
                    reportsFilter={reportsFilter}
                    setReportsFilter={setReportsFilter}
                    onOpenReportDetail={(report) => setReportDetailDialog({ open: true, report })}
                    onReportReview={handleReportReview}
                  />
                )}

                {activeTab === 'users' && (
                  <UsersTab 
                    users={data.users}
                    filteredUsers={filteredAndSortedUsers}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    userFilter={userFilter}
                    setUserFilter={setUserFilter}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onPromoteAdmin={handlePromoteAdmin}
                    onRevokeAdmin={handleRevokeAdmin}
                    onToggleActive={toggleUserActive}
                    onDeleteUser={handleDeleteUser}
                    onResetPoints={handleResetUserPoints}
                    onCreateUser={handleCreateUser}
                    onImpersonate={handleImpersonate}
                  />
                )}

                {activeTab === 'content' && (
                  <ContentTab 
                    allServices={data.allServices}
                    allCourses={data.allCourses}
                    filteredServices={filteredAndSortedServices}
                    filteredCourses={filteredAndSortedCourses}
                    contentSearch={contentSearch}
                    setContentSearch={setContentSearch}
                    serviceFilter={serviceFilter}
                    setServiceFilter={setServiceFilter}
                    courseFilter={courseFilter}
                    setCourseFilter={setCourseFilter}
                    contentSortConfig={contentSortConfig}
                    onContentSort={handleContentSort}
                    onToggleFeatured={toggleFeatured}
                    onToggleServiceActive={toggleServiceActive}
                    onToggleCourseActive={toggleCourseActive}
                    onDeleteService={(id, title) => handleDeleteWithConfirm(`/api/admin/services/${id}`, title, 'servicio')}
                    onDeleteCourse={(id, title) => handleDeleteWithConfirm(`/api/admin/courses/${id}`, title, 'curso')}
                  />
                )}

                {activeTab === 'paused' && (
                  <PausedTab 
                    pausedServices={data.pausedServices}
                    pausedCourses={data.pausedCourses}
                    pausedCount={pausedCount}
                  />
                )}

                {activeTab === 'blog' && (
                  <BlogTab />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
